import { Injectable, inject } from '@angular/core';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { APP_CONFIG } from '../config/app-config.token';
import { AppConfig } from '../config/app-config.model';
import { AuthService } from '../auth/services/auth.service';
import { PlatformService } from '../auth/services/platform.service';

@Injectable({
  providedIn: 'root'
})
export class RedirectService {
  private authService = inject(AuthService);
  private platformService = inject(PlatformService);
  private config = inject<AppConfig>(APP_CONFIG);


  post(formData: Record<string, string | number | boolean>, url: string, target: string, enctype: string) {
    if (Capacitor.isNativePlatform()) {
      this.postViaMobileRedirect(formData, url, enctype);
    } else {
      this.postViaForm(formData, url, target, enctype);
    }
  }

  /**
   * Desktop: standard form submit in the current window
   */
  private postViaForm(formData: Record<string, string | number | boolean>, url: string, target: string, enctype: string) {
    const mapForm = document.createElement('form');
    mapForm.target = target;
    mapForm.method = 'POST';
    mapForm.action = url;
    mapForm.enctype = enctype;

    for (const key in formData) {
      const mapInput = document.createElement('input');
      mapInput.type = 'hidden';
      mapInput.name = key;
      mapInput.setAttribute('value', String(formData[key]));
      mapForm.appendChild(mapInput);
    }

    document.body.appendChild(mapForm);
    mapForm.submit();
  }

  /**
   * Mobile: two-step redirect via backend intermediary.
   *
   * 1. POST form params to backend /sign-prepare → get a one-time token
   * 2. Browser.open(backend/sign-form/{token}) → backend serves HTML form
   *    that auto-submits to Saviscio from the trusted domain
   *
   * This works because the system browser loads the form from
   * myabvv-pre.abvv.be (trusted by Saviscio) instead of https://localhost.
   */
  private async postViaMobileRedirect(formData: Record<string, string | number | boolean>, actionUrl: string, enctype: string) {
    const inss = this.authService.getInss();
    if (!inss) {
      console.error('[RedirectService] No INSS available for sign-prepare');
      return;
    }

    // Include the action URL and enctype as metadata for the backend
    const payload = {
      ...formData,
      _actionUrl: actionUrl,
      _enctype: enctype
    };

    try {
      // Step 1: Send form params to backend, get a token
      const prepareUrl = `${this.config.apiGatewayUrl}/members/me/sign-prepare`;
      const token = this.authService.getAccessToken();

      const response = await CapacitorHttp.post({
        url: prepareUrl,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        data: payload
      });

      if (response.status !== 200 || !response.data?.token) {
        console.error('[RedirectService] sign-prepare failed:', response);
        return;
      }

      const signToken = response.data.token;

      // Step 2: Open the form page in the system browser via the B2B gateway
      // The B2B API is keyless (no JWT needed) — the token IS the auth
      // We derive the B2B URL from atosSmartSignReturnUrl which already points there
      const formUrl = `${this.config.atosSmartSignReturnUrl}${inss}/sign-form/${signToken}`;

      if (this.platformService.isiOS()) {
        // iOS: Open in Safari (system browser) instead of SFSafariViewController.
        // SFSafariViewController loses context when itsme/FAS external auth returns,
        // creating a stacked browser overlay that the user must close manually.
        // Safari handles the full auth chain properly and myabvv:// deep link returns to the app.
        window.open(formUrl, '_blank');
      } else {
        // Android: Use in-app browser (Chrome Custom Tabs) — handles deep links natively
        await Browser.open({ url: formUrl });
      }

    } catch (error) {
      console.error('[RedirectService] Mobile signature redirect failed:', error);
    }
  }
}
